import { useMemo } from "react"

import { colors, IconColorKeys, IconSize } from "@izettle/otto"

import { SpanIcon } from "./styles"

export type IconMetadata<TSize extends IconSize> = {
  name: string
  size: TSize
  svgOtto: string
  svgPaypal: string
}

export type SvgIconProps<TSize extends IconSize> = {
  icon: IconMetadata<TSize>
  color?: IconColorKeys | "inherit"
  block?: boolean
  alt?: string
  title?: string
  transform?: {
    x?: string
    y?: string
  }
  nonStandardSizeOverride?: IconSize
  className?: string
}

export function SvgIcon<TSize extends IconSize>({
  icon,
  alt,
  title,
  block,
  transform,
  nonStandardSizeOverride,
  color = "inherit",
  className,
  ...props
}: SvgIconProps<TSize>) {
  const colorValue = useMemo(() => {
    if (color === "inherit") {
      return color
    }
    return color === "success" || color === "warning" || color === "error"
      ? colors.icon[color][
          icon.size === IconSize.ExtraSmall ||
          icon.size === IconSize.Small ||
          icon.size === IconSize.Medium
            ? "small"
            : "large"
        ]
      : colors.icon[color]
  }, [icon, color])

  const originalSize = useMemo(() => {
    switch (icon.size) {
      case IconSize.ExtraSmall:
        return 16
      case IconSize.Small:
        return 20
      case IconSize.Medium:
        return 24
      case IconSize.Large:
        return 48
      case IconSize.ExtraLarge:
      default:
        return 96
    }
  }, [icon])

  const overriddenSize = useMemo(() => {
    switch (nonStandardSizeOverride ?? icon.size) {
      case IconSize.ExtraSmall:
        return 16
      case IconSize.Small:
        return 20
      case IconSize.Medium:
        return 24
      case IconSize.Large:
        return 48
      case IconSize.ExtraLarge:
      default:
        return 96
    }
  }, [icon, nonStandardSizeOverride])

  const adjustedSvg = useMemo(() => {
    if (!nonStandardSizeOverride) {
      return icon.svgPaypal
    }
    return icon.svgPaypal
      .replace(/width="\d*"/, `width="${overriddenSize}"`)
      .replace(/height="\d*"/, `height="${overriddenSize}"`)
      .replace(/viewBox="[\d|\s]*"/g, "")
      .replace("<svg ", `<svg viewBox="0 0 ${originalSize} ${originalSize}"`)
  }, [icon, overriddenSize, originalSize])

  return (
    <SpanIcon
      {...props}
      role="img"
      block={block}
      className={className}
      transform={transform}
      title={title}
      aria-hidden={!title}
      aria-label={alt}
      color={colorValue}
      size={overriddenSize}
      dangerouslySetInnerHTML={{
        __html: adjustedSvg,
      }}
    />
  )
}
